import React, {Component} from "react";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import DateUtil from "../../../../../utils/DateUtil";
import FileUtil from "../../../../../utils/FileUtil";
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import {SelectInput, TextInput} from "../../../../../components";
import {getClinicTimeZone} from "../../../../../utils/TimezoneUtil";
import Tooltip from "../../../../../components/tooltip/Tooltip";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const TYPE_LABELS = {
    CLAIM_SUBMISSION: 'Claim Submission',
    CLAIM_CHECK: 'Claim Check',
    PRIOR_AUTHORIZATION: 'Payer Answer'
};

// A payer answer is received, not sent, and is either applied to its referral or held for review
const isPayerAnswer = (item) => item.transactionType === 'PRIOR_AUTHORIZATION';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const RESULT_OPTIONS = [{key: true, name: 'Success'}, {key: false, name: 'Failed'}];

// With a `url` prop the modal is server-driven: lazy paging plus filters mapped to the
// backend EclaimTransactionLogSpecification params. Without it (claimId prop) it keeps
// the original one-shot claim-scoped fetch — those lists are naturally small.
export default class EclaimTransactionLogModal extends Component {

    constructor(props) {
        super(props);
        this.state = {
            items: [],
            expandedRows: null,
            xmlById: {},
            totalRecords: 0,
            first: 0,
            dateRange: null,
            success: null,
            claimId: '',
            fileName: '',
            message: ''
        };
    }

    componentDidMount() {
        if (this.props.url) {
            this.retrievePage();
        } else {
            RemotingService.getRemoteCall(
                `api/insurance/coordinator/v2/claims/claim/${this.props.claimId}/eclaim-transaction-log`,
                null,
                (items) => {
                    this.setState({items: items});
                });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
    }

    retrievePage = () => {
        const {first, dateRange, success, claimId, fileName, message} = this.state;
        const params = {page: first / PAGE_SIZE + 1, size: PAGE_SIZE};
        if (success !== null && success !== undefined) {
            params.success = success;
        }
        if (claimId) {
            params.claimId = claimId;
        }
        if (fileName) {
            params.fileName = fileName;
        }
        if (message) {
            params.message = message;
        }
        if (dateRange && dateRange[0] && dateRange[1]) {
            // Day boundaries in clinic time, matching the clinic-time Date/Time column.
            // serializeObject stringifies Date via toString, which the backend cannot parse
            params.startDate = DateUtil.switchZone(DateUtil.startOfDay(dateRange[0]), getClinicTimeZone()).toISOString();
            params.endDate = DateUtil.switchZone(DateUtil.endOfDay(dateRange[1]), getClinicTimeZone()).toISOString();
        }
        RemotingService.getRemoteCall(this.props.url, params, (result) => {
            this.setState({items: result.items || [], totalRecords: result.totalCount || 0});
        });
    }

    handleFilterChange = (field, value, debounced) => {
        clearTimeout(this.searchTimer);
        this.setState({[field]: value, first: 0}, () => {
            if (debounced) {
                this.searchTimer = setTimeout(this.retrievePage, SEARCH_DEBOUNCE_MS);
            } else {
                this.retrievePage();
            }
        });
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({dateRange, first: 0}, () => {
            // Query on a complete range or on clear; ignore the half-picked state
            if (!dateRange || (dateRange[0] && dateRange[1])) {
                this.retrievePage();
            }
        });
    }

    handlePageChange = (event) => {
        this.setState({first: event.first}, this.retrievePage);
    }

    retrieveSentXml = (item, callback) => {
        // The claim-scoped viewer has no url prop; its rows read the XML via the claims tab endpoint
        const xmlBase = this.props.url || "api/insurance/coordinator/v2/claims/eclaim-transaction-log";
        RemotingService.getRemoteCall(`${xmlBase}/${item.id}/xml`, null, callback);
    }

    downloadSentXml = (item) => {
        this.retrieveSentXml(item, (xmlText) => {
            const fileName = item.fileName
                ? item.fileName.replace(/\.xml$/i, '')
                : `Claim-Transaction_${item.id}`;
            FileUtil.saveAs(fileName, xmlText, FileUtil.FileType.XML);
        });
    }

    // The sent XML is loaded the first time its row is expanded
    handleRowToggle = (e) => {
        this.setState({expandedRows: e.data});
        this.state.items
            .filter(item => e.data && e.data[item.id] && item.hasRequestXml && this.state.xmlById[item.id] === undefined)
            .forEach(item => this.retrieveSentXml(item, (xmlText) => {
                this.setState(prevState => ({xmlById: {...prevState.xmlById, [item.id]: xmlText}}));
            }));
    }

    downloadTemplate = (item) => {
        if (!item.hasRequestXml) {
            return null;
        }
        return <Tooltip title={isPayerAnswer(item) ? "Download Received XML" : "Download Sent XML"}>
            <FontAwesomeIcon icon="file-download" size="sm" style={{cursor: 'pointer'}}
                             onClick={() => this.downloadSentXml(item)}/>
        </Tooltip>;
    }

    resultTemplate(item) {
        const code = item.resultCode !== null && item.resultCode !== undefined ? ` (${item.resultCode})` : '';
        return <span className={item.success ? 'text-success' : 'text-danger'}>
            {(isPayerAnswer(item) ? (item.success ? 'Applied' : 'Held') : (item.success ? 'Success' : 'Failed')) + code}
        </span>;
    }

    rowExpansionTemplate = (item) => {
        const preStyle = {
            backgroundColor: '#f5f5f5',
            padding: '10px',
            maxHeight: '300px',
            overflow: 'auto',
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
        };
        return (
            <div style={{padding: '10px'}}>
                {item.message && <p><strong>Message:</strong> {item.message}</p>}
                {item.fileName && <p><strong>File:</strong> {item.fileName}</p>}
                {item.errorReport &&
                <div>
                    <strong>Report:</strong>
                    <pre style={preStyle}>
                        {item.errorReport}
                    </pre>
                </div>}
                {item.hasRequestXml &&
                <div>
                    <strong>{isPayerAnswer(item) ? 'Received XML:' : 'Sent XML:'}</strong>
                    <pre style={preStyle}>
                        {this.state.xmlById[item.id] === undefined ? 'Loading...' : this.state.xmlById[item.id]}
                    </pre>
                </div>}
            </div>
        );
    }

    render() {
        const searchable = !!this.props.url;

        const resultFilter = (
            <SelectInput className="w-100" value={this.state.success}
                         options={RESULT_OPTIONS}
                         displayProperty="name" valueProperty="key" searchEnabled={false}
                         onChange={(val) => this.handleFilterChange('success', val, false)}/>
        );
        const claimIdFilter = (
            <TextInput className="w-100" value={this.state.claimId}
                       onChange={(val) => this.handleFilterChange('claimId', val, true)}/>
        );
        const fileNameFilter = (
            <TextInput className="w-100" value={this.state.fileName}
                       onChange={(val) => this.handleFilterChange('fileName', val, true)}/>
        );

        return (
            <div style={{width: searchable ? 1050 : 900, padding: "20px"}}>
                {searchable &&
                <div className="d-flex justify-content-between mb-2">
                    <TextInput style={{width: 250}} placeholder="Search in messages"
                               value={this.state.message}
                               onChange={(val) => this.handleFilterChange('message', val, true)}/>
                    <PrimeDateRangeInput value={this.state.dateRange}
                                         onChange={this.handleDateRangeChange}/>
                </div>}
                <DataTable responsive emptyMessage={"No transactions found"}
                           value={this.state.items}
                           dataKey="id"
                           expandedRows={this.state.expandedRows}
                           onRowToggle={this.handleRowToggle}
                           rowExpansionTemplate={this.rowExpansionTemplate}
                           {...(searchable ? {
                               lazy: true,
                               paginator: true,
                               paginatorPosition: "bottom",
                               rows: PAGE_SIZE,
                               first: this.state.first,
                               totalRecords: this.state.totalRecords,
                               onPage: this.handlePageChange
                           } : {})}>
                    <Column expander={true} style={{width: '30px'}}/>
                    <Column header="Version" field="versionNo" style={{width: '70px'}}/>
                    <Column header="Date/Time"
                            body={item => item.sentAt ? DateUtil.formatTzDate(item.sentAt, getClinicTimeZone(), "DD-MMM-YYYY hh:mm A") : ''}/>
                    <Column header="Type" body={item => TYPE_LABELS[item.transactionType] || item.transactionType}/>
                    {this.props.showClaimId &&
                    <Column header="Claim ID" field="claimId" style={{width: '90px'}}
                            filter={searchable} filterElement={claimIdFilter}/>}
                    <Column header="Sent By" field="sentBy"/>
                    <Column header="Result" body={this.resultTemplate}
                            filter={searchable} filterElement={resultFilter}/>
                    <Column header="File" field="fileName" style={{wordBreak: "break-all"}}
                            filter={searchable} filterElement={fileNameFilter}/>
                    <Column body={this.downloadTemplate} style={{width: '40px', textAlign: 'center'}}/>
                </DataTable>
            </div>
        );
    }
}
